import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { useGuild } from './GuildLayout.jsx';

function hexFromColor(int) {
  if (!int) return null;
  return '#' + int.toString(16).padStart(6, '0');
}

export default function ConfigAutoRole() {
  const { guildId } = useGuild();
  const [roles, setRoles] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    Promise.all([api.guildRoles(guildId), api.getConfig(guildId)])
      .then(([r, c]) => {
        setRoles(r.roles);
        if (c.auto_roles) {
          try { setSelected(new Set(JSON.parse(c.auto_roles))); }
          catch { setSelected(new Set()); }
        } else {
          setSelected(new Set());
        }
        setDirty(false);
      })
      .catch(err => setToast({ type: 'error', msg: err.message }));
  }, [guildId]);

  if (!roles) return (
    <div className="card">
      <div className="skel skel-line med" />
      <div className="skel" style={{ height: 80, marginTop: 12 }} />
    </div>
  );

  function toggleRole(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
    setDirty(true);
  }

  async function save() {
    setSaving(true);
    try {
      await api.putConfig(guildId, {
        auto_roles: selected.size ? JSON.stringify([...selected]) : null,
      });
      setDirty(false);
      setToast({ type: 'success', msg: 'Saved' });
    } catch (err) {
      setToast({ type: 'error', msg: err.message });
    } finally {
      setSaving(false);
      setTimeout(() => setToast(null), 2500);
    }
  }

  const filtered = filter ? roles.filter(r => r.name.toLowerCase().includes(filter.toLowerCase())) : roles;
  const selectedCount = [...selected].filter(id => roles.some(r => r.id === id)).length;

  return (
    <>
      <h3 style={{ marginTop: 0 }}>Auto-role on join</h3>
      <p className="muted" style={{ marginTop: '-.25rem' }}>Roles automatically granted to every new member.</p>

      <div className="card">
        <div className="row" style={{ marginBottom: '1rem', alignItems: 'center' }}>
          <input
            placeholder="Filter roles…"
            value={filter}
            onChange={e => setFilter(e.target.value)}
          />
          <div className="dim" style={{ flex: 0, fontSize: 12, whiteSpace: 'nowrap' }}>
            <strong style={{ color: 'var(--text)' }}>{selectedCount}</strong> selected
          </div>
        </div>

        {!filtered.length ? (
          <div className="empty">No roles match.</div>
        ) : (
          <div className="role-grid">
            {filtered.map(role => {
              const color = hexFromColor(role.color);
              const isSelected = selected.has(role.id);
              const disabled = !role.assignable;
              return (
                <label
                  key={role.id}
                  className={`role-toggle ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
                  title={disabled ? (role.managed ? 'Integration-managed role — Discord forbids manual assignment.' : 'SoupOS must be above this role in the role hierarchy to assign it.') : ''}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    disabled={disabled}
                    onChange={() => toggleRole(role.id)}
                  />
                  <span className="role-dot" style={{ background: color || 'var(--text-dim)' }} />
                  <span className="role-name" style={color ? { color } : undefined}>{role.name}</span>
                  {role.managed && <span className="badge" style={{ marginLeft: 'auto' }}>managed</span>}
                  {!role.managed && disabled && <span className="badge warn" style={{ marginLeft: 'auto' }}>too high</span>}
                </label>
              );
            })}
          </div>
        )}
      </div>

      <p className="dim" style={{ fontSize: 12, marginTop: '-.5rem' }}>
        Drag the <strong>SoupOS</strong> role above any role you want to auto-assign — Discord won't let the bot grant a role that sits above its own top role.
      </p>

      <div className="row actions">
        <button disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save changes'}</button>
      </div>

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}
